import type { Card, DeckManifest, FsmState, GameConfig, GameStateSnapshot } from "../types.ts";
import { AudioService } from "../services/audio.ts";
import { ScreenService } from "../services/screen.ts";
import { StorageService } from "../services/storage.ts";

export class GameEngine {
  private readonly config: GameConfig;
  private readonly manifest: DeckManifest;
  private readonly audio: AudioService;
  private readonly screen: ScreenService;
  private readonly storage: StorageService;

  private state: FsmState = "IDLE";
  private activeCategories: Set<string>;
  private drawPile: Card[] = [];
  private discardPile: Card[] = [];
  private currentCard: Card | null = null;

  private countdownTimerId: number | null = null;
  private stateChangeListeners: Array<(snapshot: GameStateSnapshot) => void> = [];

  constructor(
    config: GameConfig,
    manifest: DeckManifest,
    audio: AudioService,
    screen: ScreenService,
    storage: StorageService
  ) {
    this.config = config;
    this.manifest = manifest;
    this.audio = audio;
    this.screen = screen;
    this.storage = storage;

    // Activate all categories by default
    this.activeCategories = new Set(manifest.categories);
  }

  public subscribe(listener: (snapshot: GameStateSnapshot) => void): () => void {
    this.stateChangeListeners.push(listener);
    listener(this.getSnapshot());
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter((l) => l !== listener);
    };
  }

  public getSnapshot(): GameStateSnapshot {
    return {
      currentState: this.state,
      activeCategories: new Set(this.activeCategories),
      currentCard: this.currentCard,
      drawPileCount: this.drawPile.length,
      totalAvailableCards: this.calculateTotalAvailableCards()
    };
  }

  private emitStateChange(): void {
    const snapshot = this.getSnapshot();
    for (const listener of this.stateChangeListeners) {
      listener(snapshot);
    }
  }

  public toggleCategory(category: string): boolean {
    if (this.state !== "IDLE") return false;

    if (this.activeCategories.has(category)) {
      // Must retain at least one active category
      if (this.activeCategories.size <= 1) {
        return false;
      }
      this.activeCategories.delete(category);
    } else {
      this.activeCategories.add(category);
    }

    this.emitStateChange();
    return true;
  }

  public calculateTotalAvailableCards(): number {
    let count = 0;
    for (const cat of this.activeCategories) {
      const cards = this.manifest.cardsByCategory[cat];
      if (cards) {
        count += cards.length;
      }
    }
    return count;
  }

  public openRules(): void {
    if (this.state === "IDLE") {
      this.audio.unlock();
      this.state = "RULES_MODAL";
      this.emitStateChange();
    }
  }

  public closeRules(): void {
    if (this.state === "RULES_MODAL") {
      this.state = "IDLE";
      this.emitStateChange();
    }
  }

  public openInterestModal(): void {
    if (this.state === "IDLE") {
      this.audio.unlock();
      this.state = "INTEREST_MODAL";
      this.emitStateChange();
    }
  }

  public closeInterestModal(): void {
    if (this.state === "INTEREST_MODAL") {
      this.state = "IDLE";
      this.emitStateChange();
    }
  }

  public async registerInterest(email: string): Promise<{ success: boolean; message: string }> {
    if (!this.storage.isValidEmail(email)) {
      return { success: false, message: "Please enter a valid email address." };
    }

    this.storage.saveEmailLocally(email);

    if (this.config.preorderEndpoint) {
      void this.storage.postToEndpoint(this.config.preorderEndpoint, email);
    }

    this.closeInterestModal();
    return {
      success: true,
      message: "Interest registered! We will notify you if the physical deck goes to print."
    };
  }

  public startCountdown(onTick: (remainingSeconds: number) => void): void {
    if (this.state !== "IDLE") return;

    this.audio.unlock();
    this.rebuildDrawPile();

    this.state = "COUNTDOWN";
    this.emitStateChange();

    let remaining = this.config.countdownSeconds;
    onTick(remaining);
    this.audio.playTick(false);

    this.countdownTimerId = window.setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        onTick(remaining);
        this.audio.playTick(false);
      } else {
        this.clearCountdownTimer();
        this.audio.playTick(true);
        void this.transitionToActiveCard();
      }
    }, 1000);
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimerId !== null) {
      window.clearInterval(this.countdownTimerId);
      this.countdownTimerId = null;
    }
  }

  private async transitionToActiveCard(): Promise<void> {
    this.drawNextCard();
    this.state = "ACTIVE_CARD";
    await this.screen.enableForeheadMode();
    this.emitStateChange();
  }

  public advanceCard(): void {
    if (this.state !== "ACTIVE_CARD") return;

    this.screen.triggerHaptic(this.config.hapticFeedbackMs);
    this.audio.playCardFlip();
    this.drawNextCard();
    this.emitStateChange();
  }

  public exitGame(): void {
    this.clearCountdownTimer();
    this.screen.disableForeheadMode();
    this.state = "IDLE";
    this.currentCard = null;
    this.emitStateChange();
  }

  private rebuildDrawPile(): void {
    const cards: Card[] = [];
    for (const cat of this.activeCategories) {
      const catCards = this.manifest.cardsByCategory[cat];
      if (catCards) {
        for (const card of catCards) {
          cards.push(card);
        }
      }
    }

    this.shuffle(cards);
    this.balanceCategories(cards);
    this.drawPile = cards;
    this.discardPile = [];
  }

  private drawNextCard(): void {
    if (this.drawPile.length === 0) {
      if (this.discardPile.length === 0) {
        this.rebuildDrawPile();
      } else {
        const recycled = [...this.discardPile];
        this.shuffle(recycled);
        this.balanceCategories(recycled);

        // Anti-repetition check: ensure first card drawn is not identical to the current one
        if (this.currentCard && recycled.length > 1 && recycled[recycled.length - 1].id === this.currentCard.id) {
          const lastIdx = recycled.length - 1;
          const temp = recycled[0];
          recycled[0] = recycled[lastIdx];
          recycled[lastIdx] = temp;
        }

        this.drawPile = recycled;
        this.discardPile = [];
      }
    }

    const next = this.drawPile.pop();
    if (next) {
      if (this.currentCard) {
        this.discardPile.push(this.currentCard);
      }
      this.currentCard = next;
    }
  }

  private balanceCategories(cards: Card[]): void {
    if (this.activeCategories.size <= 1) return;

    for (let i = 2; i < cards.length; i++) {
      if (
        cards[i].category === cards[i - 1].category &&
        cards[i].category === cards[i - 2].category
      ) {
        for (let j = i + 1; j < cards.length; j++) {
          if (cards[j].category !== cards[i].category) {
            const temp = cards[i];
            cards[i] = cards[j];
            cards[j] = temp;
            break;
          }
        }
      }
    }
  }

  private shuffle<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }
  }
}
